import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PLACEHOLDER_IMAGE } from '../utils/imageProxy';

interface UsePropertiesOptions {
  featured?: boolean;
  type?: string;
  location?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
  maxBedrooms?: number;
  minSize?: number;
  maxSize?: number;
  propertyType?: string;
  zone?: string;
  totalRooms?: string;
  contractType?: string;
  statusId?: string;
  showAll?: boolean; // Flag per indicare se mostrare tutti gli immobili (per admin)
}

export function useProperties(options: UsePropertiesOptions = {}) {
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishedStatusId, setPublishedStatusId] = useState<string | null>(null);

  // Ottiene lo statusId di 'published' all'avvio se non è già specificato
  useEffect(() => {
    if (!options.statusId && !options.showAll && !publishedStatusId) {
      getPublishedStatusId();
    }
  }, [options.statusId, options.showAll, publishedStatusId]);

  // Carica gli immobili quando le opzioni cambiano o quando abbiamo l'ID dello stato pubblicato
  useEffect(() => {
    if ((options.statusId || publishedStatusId || options.showAll) && supabase) {
      fetchProperties();
    }
  }, [
    options.contractType,
    options.propertyType,
    options.zone,
    options.minPrice,
    options.maxPrice,
    options.totalRooms,
    options.statusId,
    options.featured,
    publishedStatusId,
    options.showAll
  ]);

  const getPublishedStatusId = async () => {
    try {
      const { data, error } = await supabase
        .from('property_statuses')
        .select('id')
        .eq('name', 'published')
        .single();
        
      if (error) throw error;
      if (data) {
        setPublishedStatusId(data.id);
      }
    } catch (err: any) {
      console.error('Error fetching published status ID:', err);
    }
  };

  const fetchProperties = async () => {
    if (!supabase) {
      setError('Database connection not available');
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      let query = supabase
        .from('properties')
        .select(`
          *,
          zones(*),
          provinces(*),
          property_images(*),
          property_features(
            features(*)
          ),
          property_types(*),
          property_states(*),
          property_documents(*),
          property_statuses(*)
        `);

      // Apply filters conditionally
      if (options.contractType) {
        query = query.eq('type', options.contractType);
      }
      
      if (options.location) {
        query = query.ilike('location', `%${options.location}%`);
      }
      
      if (typeof options.minPrice === 'number' && !isNaN(options.minPrice)) {
        query = query.gte('price', options.minPrice);
      }
      
      if (typeof options.maxPrice === 'number' && !isNaN(options.maxPrice) && options.maxPrice !== Infinity) {
        query = query.lte('price', options.maxPrice);
      }
      
      if (options.propertyType) {
        query = query.eq('type_id', options.propertyType);
      }
      
      if (options.zone) {
        query = query.eq('zone_id', options.zone);
      }
      
      // Filtraggio per stato pubblicazione
      if (options.statusId) {
        // Se viene fornito uno stato specifico, usa quello
        query = query.eq('status_id', options.statusId);
      } else if (!options.showAll && publishedStatusId) {
        // Altrimenti, se non è richiesto di mostrare tutti, mostra solo quelli pubblicati
        query = query.eq('status_id', publishedStatusId);
      }

      // Filtraggio per proprietà in evidenza
      if (options.featured) {
        query = query.eq('featured', true);
      }

      // Handle total rooms filter
      if (options.totalRooms) {
        const [min, max] = options.totalRooms.split('-').map(Number);
        if (!isNaN(min) && !isNaN(max)) {
          query = query
            .gte('total_rooms', min)
            .lte('total_rooms', max);
        } else if (!isNaN(min)) {
          query = query.gte('total_rooms', min);
        }
      }

      // Order by priority first, then by creation date
      query = query.order('priority', { ascending: true })
                  .order('created_at', { ascending: false })
                  .limit(100); // Add limit to prevent loading too many properties

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Transform data to include primary image URL and planimetria
      const transformedData = (data || []).map(property => {
        // Ordina le immagini: prima quelle primarie, poi per posizione
        const sortedImages = [...(property.property_images || [])].sort((a, b) => {
          if (a.is_primary && !b.is_primary) return -1;
          if (!a.is_primary && b.is_primary) return 1;
          return (a.position || 0) - (b.position || 0);
        });
        
        const primaryImage = sortedImages.length > 0 ? sortedImages[0].url : null;
        
        return {
          ...property,
          images: sortedImages.map((img: any) => img.url) || [],
          primaryImage: primaryImage || PLACEHOLDER_IMAGE,
          planimetria: property.property_documents?.find((doc: any) => doc.type === 'planimetria')?.url
        };
      });

      setProperties(transformedData);
    } catch (err: any) {
      console.error('Error fetching properties:', err);
      setError(err.message || 'Errore nel caricamento degli immobili');
    } finally {
      setLoading(false);
    }
  };

  return { properties, loading, error };
}